/**
 * Currency display (P0-TR5, NFR-6).
 *
 * The lakh grouping itself lives in `@agrofarm/shared` so the server formats
 * amounts the same way in PDF and Excel exports (P3-TR4). This module adds the
 * one client-only concern: which digits to draw them with.
 */

import { formatBDT, toMoney, type MoneyValue } from '@agrofarm/shared';

/**
 * Bangla digits ০-৯, indexed by their Western equivalent.
 *
 * Open question Q3 in docs/DECISIONS.md: NFR-6 says numbers in English digits
 * is the partners' preference but asks to confirm it. Keeping the conversion
 * written and tested means answering that question is a one-line change here
 * rather than a hunt through every screen.
 */
const BANGLA_DIGITS = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'] as const;

export type DigitStyle = 'western' | 'bangla';

/** Q3 default: Bangla labels, English digits. */
export const DEFAULT_DIGIT_STYLE: DigitStyle = 'western';

export const toBanglaDigits = (text: string): string =>
  text.replace(/\d/g, (digit) => BANGLA_DIGITS[Number(digit)] ?? digit);

export interface MoneyFormatOptions {
  /** Include the ৳ sign. Off for table columns that carry it in the header. */
  symbol?: boolean;
  /** 'auto' hides .00, 'always' shows it, 'never' drops the paisa entirely. */
  decimals?: 'auto' | 'always' | 'never';
  digits?: DigitStyle;
}

/**
 * Formats an amount that arrived from the API as a string (MONEY-2).
 *
 * The input stays a string end to end: parsing it into a JS number for display
 * would reintroduce the float problem the whole money layer exists to avoid,
 * and it is trivially avoidable here.
 */
export function formatMoney(amount: string | MoneyValue, options: MoneyFormatOptions = {}): string {
  const { digits = DEFAULT_DIGIT_STYLE, ...rest } = options;
  const formatted = formatBDT(amount, rest);
  return digits === 'bangla' ? toBanglaDigits(formatted) : formatted;
}

/** True when an amount is negative, for colouring a figure red. */
export const isNegativeAmount = (amount: string): boolean => toMoney(amount).isNegative();

/**
 * A compact label for dashboard tiles: ৳6,00,000 becomes "৳ 6 lakh".
 *
 * Lakh and crore are how the partners talk about the ৳6,00,000 capital, so a
 * break-even tile reads better in those units than in seven digits.
 */
export function formatCompactBDT(amount: string, options: { digits?: DigitStyle } = {}): string {
  const value = toMoney(amount);
  const absolute = value.abs();
  const sign = value.isNegative() ? '-' : '';

  const render = (n: string, unit: string) => `${sign}৳ ${n} ${unit}`;

  let text: string;
  if (absolute.greaterThanOrEqualTo(10_000_000)) {
    text = render(absolute.dividedBy(10_000_000).toDecimalPlaces(2).toString(), 'crore');
  } else if (absolute.greaterThanOrEqualTo(100_000)) {
    text = render(absolute.dividedBy(100_000).toDecimalPlaces(2).toString(), 'lakh');
  } else {
    text = formatBDT(amount, { symbol: true });
  }

  return (options.digits ?? DEFAULT_DIGIT_STYLE) === 'bangla' ? toBanglaDigits(text) : text;
}
